import { Dispatch, ReactNode, SetStateAction, useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Rotate } from '@components/animators/Rotate';
import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { EmptyState } from '@components/empty-state';
import { createForm, useCreateFormStore } from '@components/form';
import { useDeleteModal } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconApi } from '@components/icons/api';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconDropdown } from '@components/icons/dropdown';
import { IconDropRight } from '@components/icons/dropright';
import { IconDropside } from '@components/icons/dropside';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconCode } from '@components/icons/icon-code';
import { IconInput } from '@components/icons/input';
import { IconIntegration } from '@components/icons/integration';
import { IconSend } from '@components/icons/send';
import { IconSettings } from '@components/icons/settings';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import { LabelTextGroup } from '@components/label-text-group';
import { SizedRelativeSpinner, Spinner } from '@components/loading-spinner';
import { SearchBox } from '@components/search-box';
import { Separator } from '@components/separator';
import {
  CloseButton,
  forceHideSidebar,
  InfoButton,
  Sidebar,
  useSidebar,
} from '@components/sidebar';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { StatusOptionLabel } from '@components/status-option-label';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { FieldMappingGroup } from '@components/text-editors/addons/field-mapping';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { hourOptionsOnTheHour } from '@utils/hoursOptions';
import { dayOfMonthList } from '@utils/number-arrays';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { useListPollingEndpoints } from 'api/integrations/hooks';
import {
  EndpointMethods,
  IntegrationEndpointPollingType,
  IntegrationPollingType,
} from 'api/integrations/requests';
import {
  useCreateJourneyPollingConfig,
  useDeleteJourneyPollingConfig,
  useJourneyPollingConfig,
  useJourneyPollingLogs,
  usePollingTestRequest,
  useUpdateJourneyPollingConfig,
} from 'api/journey-polling/hooks';
import { PollingConfig } from 'api/journey-polling/requests';
import { useJourneyAttributes } from 'api/journeys/hooks';
import { Condition, ConditionType } from 'api/types';
import { FieldMappedAttributeField } from 'ui/actions/common';
import { getFieldMappingId } from 'ui/actions/helpers';
import { endpointColorMap } from 'ui/authenticated/settings/pages/integrations/common';
import { IntegrationThumbnail } from 'ui/authenticated/settings/pages/organization/lists/sync-list-sidebar';
import { ActionHeader, useRawVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

import { Conditions, processConditionsForSubmission, useConditionState } from './condition-helpers';
import { useIntegrationSearch } from './use-integration-search';

import { TriggerLogListCard } from '../common';
import { useCreateCustomURLModal } from '../modals/create-custom-url';
import { useEndpointVisibilityModal } from '../modals/polling-endpoint-visibility';
import { usePreviewPollingLog } from '../modals/preview-polling-log';

export const useScheduledPollingSidebar = () => {
  const sidebar = useSidebar(ScheduledPollingSidebar, {
    variant: 'REGULAR',
  });
  return sidebar;
};

export const PollingEndpointMethods = ['POST', 'GET'] as const;

type CustomUrlData = {
  description: string;
  endpointUrl: string;
  method: 'GET' | 'POST';
};

type Payload = {
  description: string;
  isActive: boolean;
  isAutoStart: boolean;
  [key: string]: any;
};

type Data = Payload & {
  company_traveller_name: string;
  traveller_email: string;
  company_traveller_phone: string;
  conditionType: ConditionType;
  conditions: Condition[];
  pollingInterval:
    | 'six_hours'
    | 'twelve_hours'
    | 'every_day'
    | 'every_seven_days'
    | 'every_fourteen_days'
    | 'first_of_month'
    | 'last_of_month'
    | 'once_every_month';
  dayOfMonth: number;
  startDate: Date;
  time: string;
  endDate: Date;
};

const CustomUrlForm = createForm<CustomUrlData>();
const ScheduledPollingForm = createForm<Data>();

export const ScheduledPollingSidebar = ({
  pollingConfigShortId,
  journeyShortId,
}: {
  pollingConfigShortId?: string;
  journeyShortId: string;
}) => {
  const [currentMode, setCurrentMode] = useState<'create' | 'edit'>(
    pollingConfigShortId ? 'edit' : 'create'
  );
  const [currentPollingConfigId, setCurrentPollingConfigId] = useState<string | undefined>(
    pollingConfigShortId
  );

  const handlePollingConfigCreated = (newConfigId: string) => {
    setCurrentPollingConfigId(newConfigId);
    setCurrentMode('edit');
  };

  return (
    <Sidebar>
      {currentMode === 'create' ? (
        <CreatePollingContent
          journeyShortId={journeyShortId}
          onConfigCreated={handlePollingConfigCreated}
        />
      ) : (
        <EditPollingContent pollingConfigShortId={currentPollingConfigId!} />
      )}
    </Sidebar>
  );
};

const Info = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('aboutscheduledPolling')} />
      <Text
        font="regular14"
        text={styledTranslate({
          key: 'helpTextContent',
          variables: {
            clickHereToReadMore: {
              font: 'regular14',
              text: (
                <a
                  href={
                    'https://help.journeys.50skills.app/en/articles/382553-scheduled-polling-triggers'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                >
                  {t('clickHereToReadMore')}
                </a>
              ),
            },
          },
          translations: locale,
        })}
      />
    </Stack>
  );
};

const CreatePollingContent = ({
  journeyShortId,
  onConfigCreated,
}: {
  journeyShortId: string;
  onConfigCreated: (configId: string) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const [selectedIntegrationForCustomUrl, setSelectedIntegrationForCustomUrl] =
    useState<IntegrationPollingType | null>(null);

  const showManageVisibleEndpointsModal = useEndpointVisibilityModal();
  const showCreateCustomURLModal = useCreateCustomURLModal();
  const createPollingConfig = useCreateJourneyPollingConfig();

  const integrationsQuery = useListPollingEndpoints(journeyShortId);
  const integrations = useMemo(() => integrationsQuery.data ?? [], [integrationsQuery.data]);
  const {
    setSearchStr,
    visibleIntegrations,
    collapsedState,
    expandCollapseText,
    handleExpandCollapse,
    toggleCollapse,
  } = useIntegrationSearch(integrations);
  const hasVisibleIntegrations = visibleIntegrations.length > 0;

  const handleChosenEndpoint = async (value: string) => {
    const integration = visibleIntegrations?.find((integration: IntegrationPollingType) =>
      integration.endpoints.some(
        (endpoint: IntegrationEndpointPollingType) => endpoint.shortId === value
      )
    );
    const selectedEndpoint = integration?.endpoints.find(
      (endpoint: IntegrationEndpointPollingType) => endpoint.shortId === value
    );

    if (integration && selectedEndpoint?.method) {
      const data = {
        journey: journeyShortId,
        integration: integration.shortId,
        endpoint: value,
        method: selectedEndpoint.method,
        url: selectedEndpoint.url,
      };
      const pollingConfig = await createPollingConfig.mutateAsync(data);
      onConfigCreated(pollingConfig.shortId);
    }
  };

  const handleCustomUrlIntegrationSelected = (integration: IntegrationPollingType) => {
    setSelectedIntegrationForCustomUrl(integration);
  };

  if (integrationsQuery.isLoading || createPollingConfig.isLoading) {
    return <Sidebar.Spinner />;
  }

  if (selectedIntegrationForCustomUrl) {
    return (
      <CustomUrlContent
        journeyShortId={journeyShortId}
        integration={selectedIntegrationForCustomUrl}
        onConfigCreated={onConfigCreated}
      />
    );
  }

  return (
    <>
      <Sidebar.Header>
        <Stack space={4}>
          <Stack orientation="horizontal" justify="space-between" align="center">
            <Stack space={2} orientation="horizontal" align="center">
              <IconApi color="elementIndigo100" />
              <Sidebar.HeaderTitle title={t('selectEndpoint')} />
            </Stack>
            <InfoButton>
              <Info />
            </InfoButton>
            <CloseButton />
          </Stack>
          <Stack space={2} orientation="horizontal">
            <div style={{ flexGrow: 1 }}>
              <SearchBox onChange={setSearchStr} placeholder={t('core.search')} />
            </div>
            <Button
              variant="secondary"
              iconRight={
                expandCollapseText === t('expandAll') ? <IconDropRight /> : <IconDropdown />
              }
              text={expandCollapseText}
              onClick={handleExpandCollapse}
              isDisabled={!hasVisibleIntegrations}
            />
          </Stack>
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Stack space={6}>
          <Stack space={4}>
            <Text text={t('helptext')} color="textSecondary" font="regular14" />
            <Stack orientation="horizontal">
              <Button
                variant="secondary"
                onClick={() => showManageVisibleEndpointsModal({ journeyShortId })}
                text={t('manageEndpoints')}
                iconLeft={<IconSettings />}
              />
            </Stack>
          </Stack>
          {hasVisibleIntegrations && Object.keys(collapsedState).length > 0 && (
            <Stack space={2}>
              {visibleIntegrations.map((integration, index) => (
                <IntegrationGroup
                  key={`${integration.name}-${index}`}
                  integration={integration}
                  isCollapsed={collapsedState[integration.name]}
                  toggleCollapse={() => toggleCollapse(integration.name)}
                >
                  <Stack space={2}>
                    {integration.endpoints.map((endpoint, i) =>
                      endpoint.showInPolling && endpoint.isValidPollingEndpoint ? (
                        <EndpointItem
                          key={`${endpoint.name}-${i}`}
                          endpoint={endpoint}
                          onEndpointChosen={handleChosenEndpoint}
                        />
                      ) : null
                    )}
                  </Stack>
                </IntegrationGroup>
              ))}
            </Stack>
          )}
          {!hasVisibleIntegrations && !integrationsQuery.isLoading && (
            <EmptyState
              icon={<IconIntegration size={40} />}
              header={t('noEndpoints')}
              content={t('noEndpointsDescription')}
              variant="small"
            />
          )}
          {hasVisibleIntegrations && <Separator />}
          <Stack orientation="horizontal">
            <Button
              variant="secondary"
              onClick={() =>
                showCreateCustomURLModal({
                  integrations: integrations ?? [],
                  setChosenIntegration: handleCustomUrlIntegrationSelected,
                })
              }
              text={t('addCustom')}
              iconLeft={<IconAddPlus />}
            />
          </Stack>
        </Stack>
      </Sidebar.Content>
    </>
  );
};

const EditPollingContent = ({ pollingConfigShortId }: { pollingConfigShortId: string }) => {
  const { t } = useScopedTranslate(locale);
  const pollingConfigQuery = useJourneyPollingConfig(pollingConfigShortId);
  const pollingConfig = pollingConfigQuery.data;

  if (pollingConfigQuery.isLoading || !pollingConfig) {
    return <Sidebar.Spinner />;
  }

  const tabItems = [
    {
      label: t('details'),
      content: <ScheduledPollingDetails pollingConfig={pollingConfig} />,
    },
    {
      label: t('logs'),
      content: <ScheduledPollingLogs pollingConfig={pollingConfig} />,
      tabCount: pollingConfig.logsCount > 0 ? pollingConfig.logsCount : undefined,
    },
  ];

  return (
    <ActionHeader
      icon={IconApi}
      iconColor="elementIndigo100"
      title={t('scheduledPolling')}
      tabs={tabItems}
    >
      <InfoButton>
        <Info />
      </InfoButton>
    </ActionHeader>
  );
};

const CustomUrlContent = ({
  journeyShortId,
  integration,
  onConfigCreated,
}: {
  journeyShortId: string;
  integration: IntegrationPollingType;
  onConfigCreated: (configId: string) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const createPollingConfig = useCreateJourneyPollingConfig();
  const testRequestMutation = usePollingTestRequest(journeyShortId);
  const formValues = formStore.useFormValues();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isTested, setIsTested] = useState<boolean>(false);

  const handleCustomUrlSubmit = async (data: CustomUrlData) => {
    if (isTested && !errorMessage) {
      const pollingConfig = await createPollingConfig.mutateAsync({
        journey: journeyShortId,
        integration: integration.shortId,
        url: data.endpointUrl,
        isCustomUrl: true,
        description: data.description,
        method: data.method,
      });
      onConfigCreated(pollingConfig.shortId);
    }
  };

  const handleTestUrl = async () => {
    const url = formValues.endpointUrl as string;
    const method = formValues.method as EndpointMethods;
    if (!url) return;

    const response = await testRequestMutation.mutateAsync({
      integrationShortId: integration.shortId,
      url,
      method,
    });

    setIsTested(true);
    if (response.errorMessage) {
      setErrorMessage(response.errorMessage);
    } else {
      setErrorMessage(undefined);
    }
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleCustomUrlSubmit}>
      <Sidebar.Header>
        <Stack space={4}>
          <Stack orientation="horizontal" justify="space-between" align="center">
            <Stack space={2} orientation="horizontal" align="center">
              <IconApi color="elementIndigo100" />
              <Sidebar.HeaderTitle title={t('scheduledPolling')} />
            </Stack>
            <CloseButton />
          </Stack>
        </Stack>
      </Sidebar.Header>
      <Sidebar.Content>
        <Stack space={6}>
          <HideContainer title={t('triggerDetails')}>
            <CustomUrlForm.Textarea
              id="description"
              label={t('description')}
              defaultValue=""
              maxLength={1000}
              placeholder={t('addDescription')}
            />
          </HideContainer>
          <Sidebar.Separator />
          <HideContainer title={t('configuration')}>
            <Stack space={4}>
              <Card spaceX={4} spaceY={2}>
                <Stack space={2} orientation="horizontal" align="center">
                  <IntegrationThumbnail thumbnail={integration.thumbnail} />
                  <Stack space={1}>
                    <Text text={integration.name} font="semibold14" />
                    <Text text={t('customUrl')} font="regular14" color="textSecondary" />
                  </Stack>
                </Stack>
              </Card>
              <Card space={4}>
                <Stack space={4}>
                  <InfoBox variant="indigo" text={t('infoBoxText')} />
                  <CustomUrlForm.TextField
                    id="endpointUrl"
                    label={t('customEndpointUrl')}
                    validators={['valid-http-url', 'required']}
                    onChange={() => {
                      setErrorMessage(undefined);
                      setIsTested(false);
                    }}
                  />
                  <CustomUrlForm.Select
                    id="method"
                    label={t('method')}
                    validators={['required']}
                    options={PollingEndpointMethods.map((endpointMethod) => ({
                      label: (
                        <StatusOptionLabel
                          text={endpointMethod}
                          color={endpointColorMap[endpointMethod]}
                        />
                      ),
                      value: endpointMethod,
                    }))}
                    isClearable={false}
                    defaultValue="GET"
                    onChange={() => {
                      setErrorMessage(undefined);
                      setIsTested(false);
                    }}
                  />
                  <Stack space={2} orientation="horizontal" justify="space-between">
                    <TestIndicatorComponent isTested={isTested} errorMessage={errorMessage} />
                    <Button
                      variant="secondary"
                      iconLeft={
                        testRequestMutation.isLoading ? (
                          <SizedRelativeSpinner size={24} />
                        ) : (
                          <IconSend />
                        )
                      }
                      text={testRequestMutation.isLoading ? t('testing') : t('test')}
                      onClick={handleTestUrl}
                      isDisabled={testRequestMutation.isLoading}
                    />
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          </HideContainer>
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        <Stack space={2} orientation="horizontal" align="center" justify="flex-end">
          <Button variant="secondary" onClick={() => forceHideSidebar()} text={t('core.cancel')} />
          <CustomUrlForm.Actions>
            <CustomUrlForm.Submit
              variant="primary-blue"
              text={t('core.continue')}
              isDisabled={!isTested || Boolean(errorMessage) || createPollingConfig.isLoading}
            />
          </CustomUrlForm.Actions>
        </Stack>
      </Sidebar.Footer>
    </FormWithUnsavedChangesPrompt>
  );
};

const ScheduledPollingLogs = ({ pollingConfig }: { pollingConfig: PollingConfig }) => {
  const { t } = useScopedTranslate(locale);
  const { data, isLoading } = useJourneyPollingLogs(pollingConfig.shortId);
  const showPreviewPollingLog = usePreviewPollingLog();

  if (!data || isLoading) {
    return <Spinner />;
  }

  if (data.length === 0) {
    return (
      <EmptyState
        header={t('noLogs')}
        content={t('pollingHasNotBeenUsed')}
        icon={<IconInput />}
        variant="small"
      />
    );
  }

  return (
    <Stack space={2}>
      {data.map((log) => (
        <TriggerLogListCard
          key={log.shortId}
          icon={<IconCode color="elementPurple80" />}
          title={abbreviateDate(log.triggeredOnDate, 'en')}
          description={log.triggeredOnTime}
          onClick={() =>
            showPreviewPollingLog({
              pollingConfigShortId: pollingConfig.shortId,
              pollingLogShortId: log.shortId,
            })
          }
          actions={
            log.numTravellerJourneysCreated > 0 ? (
              <Badge children={log.numTravellerJourneysCreated} />
            ) : undefined
          }
        />
      ))}
    </Stack>
  );
};

const ScheduledPollingDetails = ({ pollingConfig }: { pollingConfig: PollingConfig }) => {
  const { t } = useScopedTranslate(locale);
  const showDelete = useDeleteModal();
  const deleteMutation = useDeleteJourneyPollingConfig(pollingConfig.journeyShortId);
  const updateMutation = useUpdateJourneyPollingConfig(pollingConfig);
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const isDirty = formStore.useIsDirty();

  const [offset] = pollingConfig?.timezone?.split(' ') ?? [''];

  const [fields, setFields] = useState<string[]>([
    'company_traveller_name',
    'traveller_email',
    'company_traveller_phone',
  ]);

  const initialConditions = useMemo(
    () => pollingConfig?.conditions?.conditions ?? [],
    [pollingConfig?.conditions?.conditions]
  );

  const {
    conditions,
    setConditions,
    conditionsChanged,
    handleAddCondition,
    handleRemoveCondition,
  } = useConditionState(initialConditions);

  const url = pollingConfig.url;
  const isCustomUrl = pollingConfig.isCustomUrl;

  const pollingHour =
    (formStore.useFieldValue('time') as string) ??
    new Date(pollingConfig.startDatetime).getUTCHours().toString();

  const actionVariables = useMemo(
    () => [
      {
        id: 'payload',
        label: t('payload'),
        fields: (pollingConfig?.payloadVariables ?? []).map((variable) => ({
          label: variable.label,
          field: variable.name,
          type: variable.type,
          source: 'payload',
        })),
      },
    ],
    [t, pollingConfig?.payloadVariables]
  );

  const textVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['text'],
  });

  const emailVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['email'],
  });

  const phoneVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['phone'],
  });

  const dateVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['date'],
  });

  const fileVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['file', 'text'],
  });

  const fileMultiVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['file', 'file_multi', 'text'],
  });

  const optionsVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['select', 'text'],
  });

  const numberVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['number'],
  });

  const decimalVariableGroups = useRawVariableGroups({
    actionVariables,
    types: ['number', 'decimal'],
  });

  const baseVariableGroups = useRawVariableGroups({
    actionVariables,
    types: [
      'text',
      'email',
      'phone',
      'date',
      'options',
      'select',
      'multi_select',
      'number',
      'paragraph',
      'separator',
      'array',
      'array_of_strings',
      'boolean',
      'form',
      'select',
      'decimal',
      'webhookUrl',
    ],
  });

  const handleSubmit = async (data: Data) => {
    const formConditions = processConditionsForSubmission(conditions, formValues);
    const startDate = new Date(data.startDate);
    const startDateObject = new Date(
      Date.UTC(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        Number(data.time),
        Number(0)
      )
    );

    const payload: Payload = {
      description: data.description,
      isActive: data.isActive,
      isAutoStart: data.isAutoStart,
      pollingInterval: data.pollingInterval,
      startDatetime: startDateObject,
      endDatetime: data.endDate,
      conditions: {
        conditions: formConditions,
        conditionType: data.conditionType,
      },
      mappings: {},
      dayOfMonth: data.dayOfMonth,
    };

    payload.mappings = Object.fromEntries(fields.map((attrId) => [attrId, data[attrId]]));
    payload.variables = pollingConfig.variables.map((variable) => ({
      ...variable,
      value: data[variable.name] ?? variable.value ?? variable.default_value ?? '',
    }));

    await updateMutation.mutateAsync(payload, { onSuccess: forceHideSidebar });
  };

  const onDeleteConfirm = () => {
    deleteMutation.mutateAsync(pollingConfig.shortId, { onSuccess: forceHideSidebar });
  };

  const hasChanges = isDirty || conditionsChanged;
  const fieldMappingId = getFieldMappingId(pollingConfig?.journeyShortId ?? '');

  return (
    <FormWithUnsavedChangesPrompt
      store={formStore}
      onSubmit={handleSubmit}
      isDisabled={updateMutation.isLoading}
    >
      <Sidebar.Content>
        <Stack space={6}>
          <HideContainer title={t('triggerDetails')}>
            <ScheduledPollingForm.Textarea
              id="description"
              label={t('description')}
              defaultValue={pollingConfig?.description}
              maxLength={1000}
              placeholder={t('addDescription')}
            />
          </HideContainer>
          <Sidebar.Separator />
          <HideContainer title={t('configuration')}>
            <Stack space={4}>
              <Card spaceX={4} spaceY={2}>
                <Stack space={2} orientation="horizontal" align="flex-start">
                  {pollingConfig.logoThumbnail ? (
                    <SquareImage src={pollingConfig.logoThumbnail} />
                  ) : (
                    <IconIntegration color="elementPurpleInvert" />
                  )}
                  <LabelTextGroup
                    label={pollingConfig.integrationName}
                    text={isCustomUrl ? t('customUrl') : pollingConfig.endpointName}
                  />
                </Stack>
              </Card>
              <LabelTextGroup label={t('url')} text={url} />
              {pollingConfig.variables.map((variable) => (
                <ScheduledPollingForm.TextField
                  key={variable.name}
                  id={variable.name}
                  label={variable.label}
                  validators={['required']}
                  defaultValue={variable.value ?? variable.default_value ?? ''}
                />
              ))}
            </Stack>
          </HideContainer>
          <Sidebar.Separator />
          <HideContainer title={t('fieldMapping')} overflowType="visible">
            <FieldMap
              setFields={setFields}
              pollingConfig={pollingConfig}
              textVariableGroups={textVariableGroups}
              baseVariableGroups={baseVariableGroups}
              fileVariableGroups={fileVariableGroups}
              fileMultiVariableGroups={fileMultiVariableGroups}
              emailVariableGroups={emailVariableGroups}
              phoneVariableGroups={phoneVariableGroups}
              dateVariableGroups={dateVariableGroups}
              optionsVariableGroups={optionsVariableGroups}
              numberVariableGroups={numberVariableGroups}
              decimalVariableGroups={decimalVariableGroups}
            />
          </HideContainer>
          <Sidebar.Separator />
          <HideContainer title={t('addConditions')} caps>
            <Conditions
              conditions={conditions}
              conditionType={pollingConfig.conditions?.conditionType}
              variableGroups={baseVariableGroups}
              fieldMappingId={fieldMappingId}
              onAddCondition={handleAddCondition}
              onRemoveCondition={handleRemoveCondition}
              setConditions={setConditions}
              Form={ScheduledPollingForm}
            />
          </HideContainer>
          <Sidebar.Separator />
          <HideContainer title={t('pollingInterval')} caps>
            <Stack space={4}>
              <ScheduledPollingForm.Select
                id="pollingInterval"
                label={t('every')}
                options={[
                  { value: 'six_hours', label: t('sixHours') },
                  { value: 'twelve_hours', label: t('twelveHours') },
                  { value: 'every_day', label: t('everyDay') },
                  { value: 'every_seven_days', label: t('every7days') },
                  { value: 'every_fourteen_days', label: t('every14days') },
                  { value: 'first_of_month', label: t('firstOfEveryMonth') },
                  { value: 'last_of_month', label: t('lastOfEveryMonth') },
                  {
                    value: 'once_every_month',
                    label: styledTranslate({
                      key: 'onceEveryMonth',
                      variables: {
                        select_date: {
                          text: t('selectDate'),
                          font: 'regular14',
                          color: 'textHelper',
                        },
                      },
                      translations: locale,
                    }),
                  },
                ]}
                defaultValue={pollingConfig.pollingInterval}
                validators={['required']}
              />
              <ScheduledPollingForm.ConditionalValue
                id="pollingInterval"
                when={(value) => value === 'once_every_month'}
              >
                <ScheduledPollingForm.Select
                  id="dayOfMonth"
                  label={t('dayOfMonth')}
                  options={dayOfMonthList}
                  defaultValue={
                    pollingConfig.dayOfMonth ? pollingConfig.dayOfMonth.toString() : undefined
                  }
                  validators={['required']}
                />
                <ScheduledPollingForm.ConditionalValue
                  id="dayOfMonth"
                  when={(value) => ['29', '30', '31'].includes(value)}
                >
                  <InfoBox variant="indigo" text={t('dateNotEveryMonth')} />
                </ScheduledPollingForm.ConditionalValue>
              </ScheduledPollingForm.ConditionalValue>
              <StartDateContainer>
                <ScheduledPollingForm.DatePicker
                  id="startDate"
                  label={t('startDate')}
                  defaultValue={pollingConfig.startDatetime}
                  validators={['required']}
                  minDate={new Date()}
                />
                <ScheduledPollingForm.Select
                  id="time"
                  label={t('time')}
                  options={hourOptionsOnTheHour}
                  defaultValue={new Date(pollingConfig.startDatetime).getUTCHours().toString()}
                  validators={['required']}
                />
              </StartDateContainer>
              <Text
                font="regular14"
                text={t('pollingStartsAt', {
                  hour: pollingHour,
                  minute: '00',
                  timezone: offset,
                })}
              />
              <ScheduledPollingForm.DatePicker
                id="endDate"
                defaultValue={pollingConfig?.endDatetime ? pollingConfig.endDatetime : undefined}
                label={t('endDate')}
                placeholder="DD / MM / YYYY"
              />
            </Stack>
          </HideContainer>
          <Sidebar.Separator />
          <HideContainer title={t('options')}>
            <Stack space={4}>
              <ScheduledPollingForm.Switch
                key={`isActive-${pollingConfig.isActive}`}
                id="isActive"
                label={t('active')}
                defaultValue={pollingConfig.isActive}
              />
              <ScheduledPollingForm.Switch
                key={`isAutoStart-${pollingConfig.isAutoStart}`}
                id="isAutoStart"
                label={t('startOnSubmission')}
                defaultValue={pollingConfig.isAutoStart}
              />
            </Stack>
          </HideContainer>
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        <Stack space={2} orientation="horizontal" justify="space-between">
          <div>
            <IconButton
              icon={<IconTrash />}
              variant="ghost-purple"
              onClick={() =>
                showDelete({
                  title: t('deletePolling'),
                  description: t('deletePollingDescription'),
                  buttonText: t('core.delete'),
                  hasUserConfirmation: true,
                  onConfirm: onDeleteConfirm,
                })
              }
            />
          </div>
          <Stack space={2} orientation="horizontal" align="center" justify="flex-end">
            <ScheduledPollingForm.Actions>
              <Button variant="secondary" text={t('core.cancel')} onClick={forceHideSidebar} />
              <ScheduledPollingForm.Submit
                variant="primary-blue"
                text={t('core.save')}
                isDisabled={!hasChanges}
              />
            </ScheduledPollingForm.Actions>
          </Stack>
        </Stack>
      </Sidebar.Footer>
    </FormWithUnsavedChangesPrompt>
  );
};

const TestIndicatorComponent = ({
  isTested,
  errorMessage,
}: {
  isTested: boolean;
  errorMessage?: string;
}) => {
  const { t } = useScopedTranslate(locale);

  const Icon = errorMessage ? (
    <IconFormIndicatorError color="elementRed100" />
  ) : isTested ? (
    <IconFormIndicatorSuccess color="elementGreen100" />
  ) : (
    <IconFormIndicatorInfo color="elementIndigo100" />
  );

  const text = errorMessage ? errorMessage : isTested ? t('testSuccessful') : t('notTested');

  return (
    <Stack space={2} orientation="horizontal" align="center">
      {Icon}
      <Text
        text={text}
        font="regular14"
        color={errorMessage ? 'elementRed100' : isTested ? 'elementGreen200' : 'elementIndigo100'}
      />
    </Stack>
  );
};

const FieldMap = ({
  setFields,
  pollingConfig,
  textVariableGroups,
  fileVariableGroups,
  fileMultiVariableGroups,
  emailVariableGroups,
  phoneVariableGroups,
  dateVariableGroups,
  optionsVariableGroups,
  numberVariableGroups,
  decimalVariableGroups,
  baseVariableGroups,
}: {
  setFields: Dispatch<SetStateAction<string[]>>;
  pollingConfig: PollingConfig | null;
  textVariableGroups: FieldMappingGroup[];
  fileVariableGroups: FieldMappingGroup[];
  fileMultiVariableGroups: FieldMappingGroup[];
  emailVariableGroups: FieldMappingGroup[];
  phoneVariableGroups: FieldMappingGroup[];
  dateVariableGroups: FieldMappingGroup[];
  optionsVariableGroups: FieldMappingGroup[];
  numberVariableGroups: FieldMappingGroup[];
  decimalVariableGroups: FieldMappingGroup[];
  baseVariableGroups: FieldMappingGroup[];
}) => {
  const { t } = useScopedTranslate(locale);
  const journeyAttributeTemplatesQuery = useJourneyAttributes(pollingConfig?.journeyShortId ?? '', {
    ignore_inactive: true,
  });
  const companyTravellerAttributeTemplatesQuery = useCompanyTravellerAttributeTemplates({
    queryData: { ignore_inactive: true },
  });

  const journeyAttributes = useMemo(
    () => journeyAttributeTemplatesQuery.data ?? [],
    [journeyAttributeTemplatesQuery.data]
  );

  const travellerAttributes = useMemo(
    () => companyTravellerAttributeTemplatesQuery.data ?? [],
    [companyTravellerAttributeTemplatesQuery.data]
  );

  useEffect(() => {
    setFields((currentFields) => [
      ...currentFields,
      ...journeyAttributes.map((f) => f.shortId),
      ...travellerAttributes.map((f) => f.id),
    ]);
  }, [journeyAttributes, travellerAttributes, setFields]);

  if (
    journeyAttributeTemplatesQuery.isLoading ||
    !journeyAttributeTemplatesQuery.data ||
    !companyTravellerAttributeTemplatesQuery ||
    companyTravellerAttributeTemplatesQuery.isLoading
  ) {
    return <Spinner />;
  }
  const fieldMappingId = getFieldMappingId(pollingConfig?.journeyShortId ?? '');

  return (
    <Stack space={4}>
      <HideContainer
        title={t('travellerFields')}
        icon={<IconAttributeTraveller color="buttonIconPositive" />}
      >
        <Stack space={4}>
          <ScheduledPollingForm.FieldMappingTextEditor
            id="company_traveller_name"
            label={t('core.fullName')}
            validators={['required']}
            defaultValue={pollingConfig?.mappings['companyTravellerName']}
            variableGroups={textVariableGroups}
            fieldMappingId={fieldMappingId}
            singleLine
            plaintext
          />
          <ScheduledPollingForm.EmailField
            id="traveller_email"
            label={t('core.email')}
            validators={['required']}
            defaultValue={pollingConfig?.mappings['travellerEmail']}
            variableGroups={emailVariableGroups}
            fieldMappingId={fieldMappingId}
          />
          <ScheduledPollingForm.FieldMappingPhoneField
            id="company_traveller_phone"
            label={t('core.phone')}
            defaultValue={pollingConfig?.mappings['companyTravellerPhone']}
            variableGroups={phoneVariableGroups}
            fieldMappingId={fieldMappingId}
          />
          {travellerAttributes.map((attribute) => {
            const defaultField = pollingConfig?.mappings[attribute.shortId];
            const isUserAttribute = attribute.attributeType === 'user';
            if (isUserAttribute) {
              return null;
            }
            return (
              <FieldMappedAttributeField
                key={attribute.id}
                overwriteValueId={attribute.id}
                field={{
                  id: attribute.id,
                  value: defaultField,
                }}
                attribute={attribute}
                form={ScheduledPollingForm}
                variableGroups={baseVariableGroups}
                emailVariableGroups={emailVariableGroups}
                phoneVariableGroups={phoneVariableGroups}
                dateVariableGroups={dateVariableGroups}
                optionsVariableGroups={optionsVariableGroups}
                fileVariableGroups={fileVariableGroups}
                fileMultiVariableGroups={fileMultiVariableGroups}
                numberVariableGroups={numberVariableGroups}
                decimalVariableGroups={decimalVariableGroups}
                filePath=""
                journeyId={pollingConfig?.journeyShortId ?? ''}
              />
            );
          })}
        </Stack>
      </HideContainer>
      {journeyAttributes.length > 0 && (
        <>
          <Sidebar.Separator />
          <HideContainer
            title={t('journeyFields')}
            icon={<IconAttributeJourney color="buttonIconPositive" />}
          >
            <Stack space={4}>
              {journeyAttributes.map((attribute) => {
                const defaultField = pollingConfig?.mappings[attribute.shortId];
                const isUserAttribute = attribute.attributeType === 'user';
                if (isUserAttribute) {
                  return null;
                }
                return (
                  <FieldMappedAttributeField
                    key={attribute.shortId}
                    overwriteValueId={attribute.shortId}
                    field={{
                      id: attribute.shortId,
                      value: defaultField,
                    }}
                    attribute={attribute}
                    form={ScheduledPollingForm}
                    variableGroups={baseVariableGroups}
                    emailVariableGroups={emailVariableGroups}
                    phoneVariableGroups={phoneVariableGroups}
                    dateVariableGroups={dateVariableGroups}
                    optionsVariableGroups={optionsVariableGroups}
                    fileVariableGroups={fileVariableGroups}
                    fileMultiVariableGroups={fileMultiVariableGroups}
                    numberVariableGroups={numberVariableGroups}
                    decimalVariableGroups={decimalVariableGroups}
                    filePath=""
                    journeyId={pollingConfig?.journeyShortId ?? ''}
                  />
                );
              })}
            </Stack>
          </HideContainer>
        </>
      )}
    </Stack>
  );
};

const IntegrationGroup = ({
  integration,
  children,
  isCollapsed,
  toggleCollapse,
}: {
  integration: IntegrationPollingType;
  children: ReactNode;
  isCollapsed: boolean;
  toggleCollapse: () => void;
}) => {
  return (
    <>
      <ActionItemContainer onClick={toggleCollapse}>
        <Stack orientation="horizontal" space={2}>
          <IntegrationThumbnail thumbnail={integration.thumbnail} />
          <Stack orientation="horizontal" space={1} align="center" justify="center">
            <Text
              text={integration.name}
              font="regular14"
              style={lineClamp('-webkit-inline-box', 1, true)}
            />
            <Rotate isRotated={!isCollapsed}>
              <IconDropside color="buttonIconPositive" />
            </Rotate>
          </Stack>
        </Stack>
      </ActionItemContainer>
      <Collapse isCollapsed={isCollapsed}>{children}</Collapse>
    </>
  );
};

const EndpointItem = ({
  endpoint,
  onEndpointChosen,
}: {
  endpoint: IntegrationEndpointPollingType;
  onEndpointChosen: (value: string) => void;
}) => {
  return (
    <ActionItemContainer onClick={() => onEndpointChosen(endpoint.shortId)}>
      <Stack orientation="horizontal" align="center" space={2} justify="space-between">
        <Text
          text={endpoint.name}
          font="regular14"
          style={lineClamp('-webkit-inline-box', 1, true)}
        />
        <Tag
          text={endpoint.method}
          variant={endpoint.method === 'GET' ? 'green' : 'blue'}
          size="small"
        />
      </Stack>
    </ActionItemContainer>
  );
};

const ActionItemContainer = styled.button({
  borderRadius: pxToRem(4),
  paddingLeft: pxToRem(8),
  paddingRight: pxToRem(8),
  lineHeight: pxToRem(20),
  minHeight: pxToRem(40),
  ':hover': {
    backgroundColor: theme.color.uiTrans5,
    cursor: 'pointer',
  },
  '> div': {
    width: '100%',
  },
  '&[data-disabled="true"]': {
    cursor: 'not-allowed',
  },
});

const StartDateContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 4,
})({
  '>div': {
    width: '100%',
  },
});

export const locale = {
  en: {
    details: 'Details',
    logs: 'Logs',
    selectEndpoint: 'Select or add custom endpoint',
    scheduledPolling: 'Scheduled polling',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    helptext:
      'Select the endpoint you want to monitor and use for scheduled polling from your added integrations.',
    manageEndpoints: 'Manage visible endpoints',
    addCustom: 'Add custom URL',
    triggerDetails: 'Trigger details',
    description: 'Description',
    addDescription: 'Add description',
    configuration: 'Configuration',
    customUrl: 'Custom URL',
    customEndpointUrl: 'Custom endpoint URL',
    notTested: 'Not tested',
    testSuccessful: 'Test successful',
    test: 'Test',
    testing: 'Testing',
    fieldMapping: 'Field mapping',
    travellerFields: 'Traveller profile fields',
    journeyFields: 'Journey fields',
    addConditions: 'Add conditions',
    pollingInterval: 'Polling interval',
    every: 'Every',
    startDate: 'First poll',
    time: 'Time',
    endDate: 'End date',
    options: 'Options',
    active: 'Active',
    startOnSubmission: 'Start on submission',
    noEndpoints: 'No endpoints added',
    noEndpointsDescription: 'Select the endpoints you want available for scheduled polling',
    infoBoxText:
      'Enter the endpoint URL you want to poll. Once entered, you will need to test the URL to proceed with the configuration',
    sixHours: '6 hours',
    twelveHours: '12 hours',
    everyDay: 'Every day',
    every7days: 'Every 7 days',
    every14days: 'Every 14 days',
    firstOfEveryMonth: 'First of every month',
    datePassed: 'This date or time has passed and the next poll will be on the {date} at {time}',
    conditionsHelpText: 'Set the conditions that determine when travellers should be added',
    payload: 'Payload',
    deletePolling: 'Delete Scheduled Polling',
    deletePollingDescription: 'Are you sure you want to delete the scheduled polling?',
    method: 'Method',
    url: 'URL',
    pollingHasNotBeenUsed: 'This scheduled polling has not been used',
    noLogs: 'No logs',
    pollingStartsAt: 'Polling starts at {hour}:{minute} {timezone}',
    lastOfEveryMonth: 'Last of every month',
    onceEveryMonth: 'Once every month {select_date}',
    selectDate: '(select date)',
    dayOfMonth: 'Select day of the month',
    dateNotEveryMonth:
      "This date isn't in every month — the schedule will use the last day instead",
    aboutscheduledPolling: 'About scheduled polling',
    helpTextContent:
      'This trigger automatically checks a chosen endpoint at regular intervals to see if new data is available. It saves you from having to fetch updates manually.\n\n{clickHereToReadMore}',
    clickHereToReadMore: 'Click here to read more',
  },
};
